/**
 * KAN-755: вкладка "Скидки": поиск, фильтры по типу и статусу, таблица с
 * пагинацией, создание и правка (DiscountFormDialog), удаление с
 * подтверждением. Кнопки изменения только владельцу.
 */
import { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { PaginationBar } from '@/components/common/PaginationBar';
import { ConfirmDialog } from '@/components/telegram/ConfirmDialog';
import { useDebounce } from '@/hooks/useDebounce';
import { useDiscountMutations, useDiscounts, useDiscountTypes } from '@/hooks/useDiscounts';
import { usePageSize } from '@/hooks/usePageSize';
import { DISCOUNT_STATE_VIEW, DISCOUNT_STATES } from '@/lib/discountForm';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import type { DiscountListItem, DiscountState } from '@/types/discount';
import { DiscountFormDialog } from './DiscountFormDialog';
import { DiscountsTable } from './DiscountsTable';

const STATE_OPTIONS = [
  { value: '', label: 'Все статусы' },
  ...DISCOUNT_STATES.map((state) => ({ value: state, label: DISCOUNT_STATE_VIEW[state].label })),
];

const isState = (value: string): value is DiscountState => (DISCOUNT_STATES as readonly string[]).includes(value);

export function DiscountsTab({ canEdit }: { canEdit: boolean }) {
  const [input, setInput] = useState('');
  const search = useDebounce(input.trim(), 300);
  const [formula, setFormula] = useState('');
  const [state, setState] = useState<DiscountState | ''>('');
  const { page, setPage, pageSize, setPageSize } = usePageSize('discounts');
  const types = useDiscountTypes();
  const list = useDiscounts({ search, formula, state, page, page_size: pageSize });
  const { remove } = useDiscountMutations();

  const [dialog, setDialog] = useState<{ open: boolean; id: string | null }>({ open: false, id: null });
  const [toDelete, setToDelete] = useState<DiscountListItem | null>(null);

  const items = list.data?.results ?? [];
  const typeOptions = [
    { value: '', label: 'Все типы' },
    ...(types.data ?? []).map((t) => ({ value: t.code, label: t.label })),
  ];

  let content: JSX.Element;
  if (list.isLoading) content = <p className="text-sm text-gray-500">Загрузка...</p>;
  else if (list.isError) content = <p className="text-sm text-red-700">{extractErrorMessage(list.error, 'Не удалось загрузить скидки')}</p>;
  else if (items.length === 0) {
    content = (
      <p className="rounded-lg border bg-white p-6 text-center text-sm text-gray-500" data-testid="discounts-empty">
        {search || formula || state ? 'Ничего не найдено' : 'Скидок пока нет'}
      </p>
    );
  } else {
    content = (
      <>
        <DiscountsTable
          items={items}
          canEdit={canEdit}
          onEdit={(item) => setDialog({ open: true, id: item.id })}
          onDelete={setToDelete}
        />
        <PaginationBar
          className="mt-3"
          page={page}
          pageSize={pageSize}
          count={list.data?.count ?? 0}
          hasNext={!!list.data?.next}
          hasPrevious={!!list.data?.previous}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </>
    );
  }

  return (
    <div className="space-y-3" data-testid="discounts-tab">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            type="search"
            aria-label="Поиск скидки"
            placeholder="Название"
            className="pl-9"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <NativeSelect
          aria-label="Тип скидки"
          className="sm:w-48"
          options={typeOptions}
          value={formula}
          onChange={(e) => {
            setFormula(e.target.value);
            setPage(1);
          }}
        />
        <NativeSelect
          aria-label="Статус"
          className="sm:w-44"
          options={STATE_OPTIONS}
          value={state}
          onChange={(e) => {
            setState(isState(e.target.value) ? e.target.value : '');
            setPage(1);
          }}
        />
        {canEdit && (
          <Button className="sm:ml-auto" onClick={() => setDialog({ open: true, id: null })} data-testid="discount-create">
            <Plus className="mr-1 h-4 w-4" />
            Новая скидка
          </Button>
        )}
      </div>
      {remove.error && (
        <p role="alert" className="text-sm text-red-700">
          {extractErrorMessage(remove.error, 'Не удалось удалить скидку')}
        </p>
      )}
      {content}
      {canEdit && (
        <>
          <DiscountFormDialog
            open={dialog.open}
            discountId={dialog.id}
            onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
          />
          <ConfirmDialog
            open={toDelete !== null}
            onOpenChange={(open) => {
              if (!open) setToDelete(null);
            }}
            testId="discount-delete-confirm"
            view={{
              title: `Удалить скидку "${toDelete?.name ?? ''}"?`,
              description: 'Скидка перестанет действовать на новые расчёты. Оформленные заказы не меняются.',
              confirm: 'Удалить',
            }}
            onConfirm={() => {
              if (toDelete) remove.mutate(toDelete.id);
              setToDelete(null);
            }}
          />
        </>
      )}
    </div>
  );
}
